import { useNavigate } from "react-router";
import { useTenant } from "../../Contexts/TenantContext";
import { FaCheckCircle, FaWhatsapp } from "react-icons/fa";
import { toast } from "react-toastify";
import Button from "../Button/Button";

type OrderCompletedStepProps = {
  orderNumber: number;
  awaitingConfirmation?: boolean;
  processingPayment?: boolean;
  customTitle?: string;
  customDescription?: string;
  isStorePickup?: boolean;
  pickupDays?: number | null;
};

const OrderCompletedStep = ({
  orderNumber,
  awaitingConfirmation = false,
  processingPayment = false,
  customTitle,
  customDescription,
  isStorePickup = false,
  pickupDays
}: OrderCompletedStepProps) => {
  const navigate = useNavigate();

  const { tenantContact } = useTenant();

  const handleNavigateToMyOrders = () => {
    navigate("/pedidos");
  };

  const handleNavigateToHome = () => {
    navigate("/");
  };

  const handleContactCustomerSupport = () => {
    if (!tenantContact?.whatsAppNumber || !orderNumber) {
      toast.error("Informações de contato ou número do pedido não estão disponíveis.");
      return;
    }

    const message = `Olá, fiz um pedido de n° ${orderNumber}. Estou aguardando a entrega do pedido, poderia me atualizar sobre o mesmo?`;
    const encodedMessage = encodeURIComponent(message);
    const url = `https://wa.me/${tenantContact.whatsAppNumber}?text=${encodedMessage}`;
    
    window.open(url, "_blank");
  };

  const title = customTitle ?? (processingPayment
    ? "Pagamento em processamento"
    : awaitingConfirmation
      ? "Pagamento gerado com sucesso!"
      : "Pagamento do pedido confirmado com sucesso!");

  const pickupTimeText = pickupDays
    ? `em até ${pickupDays} ${pickupDays > 1 ? "dias úteis" : "dia útil"}`
    : "no prazo informado";

  const description = customDescription ?? (processingPayment
    ? "Recebemos os dados do cartão e estamos aguardando a confirmação do pagamento. Avisaremos assim que for aprovado."
    : awaitingConfirmation
      ? isStorePickup
        ? `Assim que o pagamento for confirmado, seu pedido será separado e você poderá retirá-lo ${pickupTimeText}.`
        : "Assim que o pagamento for confirmado, o seu pedido será separado e enviado o mais rápido possível."
      : isStorePickup
        ? `Agora é só aguardar. Seu pedido será separado e você poderá retirá-lo ${pickupTimeText}.`
        : "Agora é só aguardar. O seu pedido será separado e enviado o mais rápido possível.");

  return (
    <div className="flex flex-col items-center text-center w-full p-4 md:p-6 gap-3">
      <FaCheckCircle className="text-primary-600 text-3xl md:text-4xl" />
      <h2 className="text-xl font-bold text-primary-600">{title}</h2>
      <p className="text-sm md:text-base">{description}</p>
      <div className="w-full flex flex-col md:flex-row gap-2 md:gap-4 mt-2">
        <Button 
          className="w-full py-2"
          onClick={handleNavigateToMyOrders}
        >
          {"Ir para meus pedidos"}
        </Button>
        <Button variant="secondary" 
          className="w-full py-2"
          onClick={handleNavigateToHome}
        >
          {"Voltar para a tela inicial"}
        </Button>
        <Button variant="whatsapp" 
          className="flex justify-center items-center gap-2 w-full py-2"
          onClick={handleContactCustomerSupport}
        >
          <FaWhatsapp />
          {"Falar com o atendimento"}
        </Button>
      </div>
    </div>
  );
};

export default OrderCompletedStep;
